import React from "react"
import {
  Box, Flex, Input, Select, Stack, StackDivider, Text,
} from "@chakra-ui/react"
import { range } from "lodash"
import {
  DioboxEvent, Form, FormStatus,
} from "sharedTypes"
import { formStatusAndColor, isListedOnPortal } from "utilities/forms"
import { asMoney } from "utilities/strings"
import useResolvedTranslations from "pages/EventShow/Settings/LanguageTemplates/useResolvedTranslations"

type Props = {
  event: DioboxEvent
  forms: Form[]
}

const FormSelection = ({ event, forms }: Props) => (
  <Stack
    spacing={0}
    divider={<StackDivider />}
    borderLeftWidth={1}
    borderRightWidth={1}
    borderBottomWidth={1}
    borderBottomRadius="lg"
    overflow="hidden"
  >
    {forms.filter((form) => isListedOnPortal(form, event)).map((form, index) => (
      <FormRow
        key={form.id}
        index={index}
        form={form}
        event={event}
      />
    ))}
  </Stack>
)

const FormRow = ({
  form,
  event,
}: {
  index: number
  form: Form
  event: DioboxEvent
}) => {
  const { status: formStatus } = formStatusAndColor(form, event)
  const closed = formStatus !== FormStatus.Published
  const translations = useResolvedTranslations(form.languageTemplateId)

  return (
    <Box minH="50px" backgroundColor="white" p={5}>
      <Flex
        justify="space-between"
        {...(closed ? { pointerEvents: "none", opacity: "0.4" } : {})}
      >
        <Flex>
          <Box minW="75px" mr={4}>
            <QuantitySelect max={form.limitPerSubmission} />
          </Box>
          <Box mr={4}>
            <Text fontWeight="bold">{form.name}</Text>
            <Text>{form.description}</Text>
          </Box>
        </Flex>
        <Box>
          {!closed && (
          <>
            {!form.variablePrice && isPaid(form.price) && (
              <Text fontWeight="bold">
                {asMoney(form.price, form.currency)}
              </Text>
            )}
            {form.variablePrice && (
              <Input w={20} value={`0 ${form.currency}`} />
            )}
          </>
          )}
          {/* Full forms show the closing message too: the preview is for styling */}
          {closed && (
          <Text fontWeight="bold" color="red.500">
            {
              translations[
                `${form.type.toLowerCase()}ClosingMessage` as keyof typeof translations
              ]
            }
          </Text>
          )}
        </Box>
      </Flex>
    </Box>
  )
}

const QuantitySelect = ({
  max,
}: {
  max: number | null
}) => (
  <Select size="xs">
    {range(0, (max || 10) + 1).map((k) => ({
      label: k.toString(),
      value: k.toString(),
    })).map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </Select>
)

const isPaid = (price: string | null) => price && parseFloat(price) > 0

export default FormSelection
